import React, { useState, useCallback, useEffect } from 'react';
import axios from '../../../../api/apiClient';
import { token, domain } from '../../../Security';
import {
  CreateTableNew as CreateTable,
  SearchBar,
  GeneralMessage,
  useGeneralMessage
} from '../../../../common/APICommon';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoadingSpinner } from '../../../../common/Loading';
import { EmployeeRequestModal } from '../forms/EmployeeRequestModal';
import { formatDateLong } from '../../../../utils/textUtils';
import { getStatusConfig } from '../../../../utils/enums';

export const Approver = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);

  const apiUrl = domain + "/api/EmployeeRequest";

  const headers = [
    "ID",
    "Request Number",
    "Employee No",
    "Employee Name",
    "Vessel",
    "Request Date",
    "Status",
    "Actions"
  ];

  const accessors = [
    "id",
    "requestNumber",
    "employeePersonalInformationEmployeeId", // Matches DTO
    "employeePersonalInformationFullName",   // Matches DTO
    "vesselName",                             // Matches DTO
    "requestDate",
    "status",
    "actions"
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Get the ID of the logged-in user
      const currentUserId = user?.employeePersonalInformationId;

      // Send the request filtered by approverId
      const response = await axios.get(`${apiUrl}?approverId=${currentUserId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showGeneralMessage("Error fetching requests", false);
    } finally {
      setLoading(false);
    }
  }, [user, apiUrl, showGeneralMessage]); // Added dependencies

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    const res = filteredData.filter((f) =>
      f?.employeeName?.toLowerCase().includes(lowercaseValue) ||
      f?.requestNumber?.toLowerCase().includes(lowercaseValue) ||
      f?.vesselName?.toLowerCase().includes(lowercaseValue)
    );
    setData(res);
  };

  const viewHandleClick = (item) => {
    setSelectedData(item);
    setShowModal(true);
  };

  const actions = [
    {
      name: "View & Action",
      tooltip: "view",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-sky-500 hover:bg-sky-600 flex items-center",
      function: viewHandleClick,
      visible: true,
    },
  ];


  const renderCustomCell = (row, accessor) => {
    if (accessor === 'status') {
      const config = getStatusConfig(row.status);

      return (
        <span className={`px-3 py-1 text-[11px] rounded-full font-bold border uppercase tracking-wide
        ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
          {row.status}
        </span>
      );
    }

    if (accessor === 'requestDate') {
      return formatDateLong(row.requestDate);
    }
    return null;
  };

  return (
    <div>
      <SearchBar handleFilter={handleFilter} />

      <CreateTable
        tableName={"EmployeeRequestsApproval"}
        headers={headers}
        accessors={accessors}
        data={data}
        actions={actions}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={data?.length}
        customCellRenderer={renderCustomCell}
      />

      <EmployeeRequestModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); fetchData(); }}
        mode="approve" // Specific mode for Approver
        selectedData={selectedData}
        showGeneralMessage={showGeneralMessage}
        user={user}
      />

      <LoadingSpinner loading={loading} />

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />
    </div>
  );
};